import React, { useState } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  useTheme,
} from "@mui/material";
import {
  MenuOpen,
  Menu,
  SettingsBrightness,
  Logout,
  Print,
  Dashboard,
  SettingsSuggest,
  Group,
  FitScreen,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { logout } from "../../store/authSlice";
import ThemeToggle from "../buttons/ThemeToggle";
import LogoSvg from "../../assets/images/logo.svg";
import { blueGrey, yellow } from "@mui/material/colors";
import FullscreenToggle from "../buttons/FullscreenToggle";

const SIDEBAR_WIDTH = 300;
const SIDEBAR_COLLAPSED_WIDTH = 72;

const SideBar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [collapsed, setCollapsed] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const topNavLinks = [
    { name: "Dashboard", icon: <Dashboard />, link: "/" },
    { name: "Print Requests", icon: <Print />, link: "/requests" },
    { name: "Services", icon: <SettingsSuggest />, link: "/services" },
    { name: "Configuration", icon: <Group />, link: "/configuration" },
  ];

  const bottomNavLinks = [
    {
      name: <FullscreenToggle />,
      icon: <FitScreen />,
    },
    {
      name: (
        <Box display="flex" gap={2} alignItems="center">
          <span>Preference</span>
          <ThemeToggle />
        </Box>
      ),
      icon: <SettingsBrightness />,
    },
    { name: "Logout", icon: <Logout /> },
  ];

  const handleLogoutDialog = {
    open: () => setLogoutDialogOpen(true),
    confirm: () => {
      setLogoutDialogOpen(false);
      dispatch(logout());
      navigate("/login");
    },
    cancel: () => setLogoutDialogOpen(false),
  };

  const renderLinkItem = (link, index) => {
    const isActive = link.link === location.pathname;

    const handleClick = () => {
      if (link.name === "Logout") {
        handleLogoutDialog.open();
      } else if (link.link) {
        navigate(link.link);
      }
    };

    return (
      <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
        <Tooltip title={collapsed ? link.name : ""} placement="right" arrow>
          <ListItemButton
            selected={isActive}
            onClick={handleClick}
            sx={{
              minHeight: 48,
              px: collapsed ? 1.5 : 3,
              borderRadius: 2,
              bgcolor: isActive ? theme.palette.action.selected : "transparent",
              "&:hover": { bgcolor: theme.palette.action.hover },
              justifyContent: collapsed ? "center" : "flex-start",
            }}
          >
            <ListItemIcon
              sx={{
                color: isActive ? yellow[500] : yellow[700],
                minWidth: 0,
                mr: collapsed ? 0 : 2,
                justifyContent: "center",
              }}
            >
              {link.icon}
            </ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary={link.name}
                primaryTypographyProps={{
                  fontWeight: isActive ? 500 : 400,
                  fontSize: 16,
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  };

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
          overflow: "auto",
          borderRadius: 0,
          borderRight: `1px ${theme.palette.divider} solid`,
          transition: "width 0.2s",
          bgcolor: blueGrey[900],
          color: "white",
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack py={2} spacing={2} sx={{ flexGrow: 1 }}>
          {/* Logo and Collapse Button */}
          <Box
            display="flex"
            alignItems="center"
            px={collapsed ? 1 : 2}
            justifyContent={collapsed ? "center" : "space-between"}
          >
            <Box display="flex" alignItems="center" gap={1}>
              {!collapsed && (
                <img
                  src={LogoSvg}
                  alt="brand"
                  style={{ width: 40, height: 40, filter: "invert(1)" }}
                />
              )}
              {!collapsed && (
                <Typography variant="h6" fontWeight={700}>
                  Zerox Cloud
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={() => setCollapsed((prev) => !prev)}
              sx={{
                ml: collapsed ? 0 : 1,
                color: yellow[400],
                bgcolor: theme.palette.action.selected,
                "&:hover": { bgcolor: theme.palette.action.hover },
              }}
            >
              {collapsed ? <Menu /> : <MenuOpen />}
            </IconButton>
          </Box>

          <Divider
            sx={{ bgcolor: theme.palette.divider, mx: collapsed ? 1 : 2 }}
          />

          {/* Top Navigation */}
          <List sx={{ px: 1 }}>
            {topNavLinks.map((link, index) => renderLinkItem(link, index))}
          </List>
        </Stack>

        {/* Bottom Navigation */}
        <List sx={{ px: 1, mb: 2 }}>
          {bottomNavLinks.map((link, index) =>
            renderLinkItem(link, index + topNavLinks.length)
          )}
        </List>
      </Paper>

      {/* Logout Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutDialog.cancel}
        aria-labelledby="logout-dialog-title"
      >
        <DialogTitle id="logout-dialog-title">Logout Confirmation</DialogTitle>
        <DialogContent sx={{ minWidth: { md: "540px" } }}>
          <DialogContentText>
            Are you sure you want to logout?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ mb: 2, px: 2 }}>
          <Button onClick={handleLogoutDialog.cancel}>Cancel</Button>
          <Button
            onClick={handleLogoutDialog.confirm}
            variant="contained"
            autoFocus
          >
            Sure
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SideBar;
